import {gsap} from "gsap";
import {getOrientation} from "./utilities";
import {ScrollTrigger} from "gsap/ScrollTrigger";
import {scrollTriggerConfig, duration, scrolling} from "./animation";
import {setActiveNavItem} from "./nav";
import {videos, INITIAL_VARIANT, playVideos} from "./variants";

const animateIntro = (skip = false) => {
  const sectionId = 'intro';
  const orientation = getOrientation();
  const sectionDuration = duration[sectionId];
  let introPlayed = false;

  const currentVideo = videos.find(video => video.dataset.variant === INITIAL_VARIANT);
  currentVideo.addEventListener('loadedmetadata', playIntro);

  // Selectors
  const el = {
    nav: '.nav',
    main: {
      sections: '.main__sections',
    },
    intro: {
      section: '.section-intro',
      body: '.section-intro .section__body',
      main1: '.section-intro__main-1',
      main2: '.section-intro__main-2',
      mainMobile: '.section-intro-mobile',
      mobileBody: '.section-intro-mobile .section__body',
      main2Title: '.section-intro__main-2 .section-intro__title',
      main2Text: '.section-intro__main-2 .section-intro__text',
      bg: '.section-intro__bg',
      bgOverlay: '.section-intro__bg-overlay',
      media: '.section-intro__bg-media',
      video: '.section-intro__video',
      mask: '.section-intro__bg-mask',
      scroll: '.section-intro__scroll',
      award: '.section-intro__award',
      variants: '.section-intro__variants',
    },
  };

  function playIntro() {
    if (introPlayed) return;

    const hiddenElements = [
      el.intro.main1,
      el.intro.main2,
      el.intro.scroll,
    ];
    gsap.set(hiddenElements, { autoAlpha: 0 });
    gsap.to(el.intro.section, { autoAlpha: 1, duration: 1 });

    // Intro timeline
    const introTimeline = gsap.timeline({
        scrollTrigger: {
          id: sectionId,
          trigger: el.intro.section,
          start: `50% 75%`,
          end: `50% 0%`,
          toggleClass: 'section--active',
          onEnter: (self) => {
            setActiveNavItem();
            document.querySelector('html').classList.add('scroll-lock');
          },
          onLeave: (self) => {
            gsap.timeline()
              .to(el.intro.body, { autoAlpha: 1, duration: 0 }, 0)
              .to(el.intro.main2, { y: '-100vh', autoAlpha: 0, duration: sectionDuration }, 0)
              .to(el.intro.variants, { y: '-20vh', duration: sectionDuration }, 0)
              .to(el.intro.bg, { y: '-100vh', duration: sectionDuration }, 0)
              .to(el.intro.award, { autoAlpha: 0, duration: sectionDuration }, 0)
              .to(el.intro.scroll, { autoAlpha: 0, duration: sectionDuration }, 0);

              document.querySelector('html').classList.remove("scroll-lock");
          },
          onEnterBack: (self) => {
            setActiveNavItem();

            gsap.timeline()
              .to(el.intro.main2, { y: 0, autoAlpha: 1, duration: sectionDuration }, 0)
              .to(el.intro.variants, { y: 0, duration: sectionDuration }, 0)
              .to(el.intro.bg, { y: 0, duration: sectionDuration }, 0)
              .to(el.intro.award, { autoAlpha: 1, duration: sectionDuration }, 0);
              // .to(el.intro.scroll, { autoAlpha: 1, duration: sectionDuration }, 0)
            document.querySelector('html').classList.add('scroll-lock');
          },
        },
      })
        .addLabel('intro-part1')
        .add(playIntroPart1())
        .addLabel('intro-part2')
        .add(playIntroPart2())
        .addLabel('intro-end')
        // Skip intro
        .seek(skip ? 'intro-end' : 0, false);

    introPlayed = true;
  }

  // Intro parts
  function playIntroPart1() {
    function animate(element, orientation) {
      const vars = {
        media: {
          portrait: { xPercent: '+=10' },
          landscape: {},
        },
        mask: {
          portrait: { scale: 3 },
          landscape: { scale: 2 },
        },
        main1: {
          portrait: { y: '30vh' },
          landscape: { y: '10vh' },
        },
      };

      return vars[element][orientation];
    }

    return gsap.timeline()
      .to(el.intro.media,{ ...animate('media', orientation), yPercent: -40, scale: 1.2, duration: 1.4 }, 0)
      .to(el.intro.mask, { ...animate('mask', orientation), transformOrigin: 'bottom', opacity: 0, duration: 1.4 }, '<')
      .to(el.intro.bgOverlay, { opacity: 0, duration: 1.4 }, '<')
      .fromTo(el.intro.main1, { y: '75vh' }, { ...animate('main1', orientation), autoAlpha: 1, duration: 1.2, onComplete: playVideo }, '<.2')
    ;
  }

  function playIntroPart2() {
    ScrollTrigger.create({
      trigger: ".section-intro-mobile",
      start: "0% 75%",
      end: "50% 0%",
      toggleClass: 'section--active',
      onLeave: (self) => {
        gsap.timeline()
          .to('.section-intro__main-2-mobile', { y: '-100vh', autoAlpha: 0, duration: sectionDuration }, 0)
          .then(scrolling.disable);
      },
      onEnter: (self) => {
        gsap.timeline()
          .fromTo('.section-intro__main-2-mobile', { y: '40vh', autoAlpha: 0 }, { y: 0, autoAlpha: 1 }, 0)
          .then(scrolling.enable);
      },
      onEnterBack: (self) => {
        gsap.timeline()
          .fromTo('.section-intro__main-2-mobile', { y: '-40vh', autoAlpha: 0 }, { y: 0, autoAlpha: 1 }, 0)
          .then(scrolling.enable);
      },
      onLeaveBack: (self) => {
        gsap.timeline()
          .fromTo('.section-intro__main-2-mobile', { y: 0, autoAlpha: 1 }, { y: "40vh", autoAlpha: 0 }, 0)
          .then(scrolling.enable);
      }
    });

    function animate(element, orientation) {
      const vars = {
        media: {
          portrait: { xPercent: '+=2.5', scale: 1.05 },
          landscape: { xPercent: '+=1.5', scale: 1.1 },
        },
      };

      return vars[element][orientation];
    }

    return gsap.timeline({
      defaults: {
        duration: 1.5,
        ease: 'power2',
      },
    })
      .to(el.intro.media, { ...animate('media', orientation), yPercent: -50, ease: 'power1.inOut', duration: 1, delay: 4 }, '+=.5')
      .to(el.intro.main1, { y: '-50vh', opacity: 0, ease: 'power1.inOut' }, '<.7')
      .fromTo(el.nav, { y: '50vh', autoAlpha: 0 }, { y: 0, autoAlpha: 1 }, '<1')
      .to(el.intro.main2, { autoAlpha: 1 }, '<')
      .from(el.intro.main2Title, { y: '40vh' }, '<')
      .from(el.intro.main2Text, { y: '45vh' }, '<')
      .from(el.intro.award, { y: '60vh', autoAlpha: 0 }, '<')
      .fromTo(el.intro.scroll, { y: '40vh', autoAlpha: 0 }, { y: 0, autoAlpha: 1 }, '<2.5')
    ;
  }

  // Video
  function playVideo() {
    const revealEvent = skip ? 'play' : 'ended';
    currentVideo.addEventListener(revealEvent, revealSections);
    playVideos(videos);
  }

  function revealSections() {
    document.body.classList.remove('intro-playing');
    document.querySelector('.section-intro').classList.add('section-intro__finished');
    document.querySelector('html').classList.add('intro-finished');
  }

}

export default animateIntro;
